<template>
  <div class="call-detail-page">
    <!-- Page Content Container -->
    <div class="page-content">
      <!-- Page Header Area -->
      <div class="page-header">
        <div class="header-content">
          <div class="left-section">
            <!-- Back Button -->
            <v-btn
              icon
              size="small"
              variant="text"
              @click="goBack"
              class="back-btn">
              <v-icon size="20" color="on-surface">
                mdi-chevron-left
              </v-icon>
            </v-btn>

            <!-- Page Title -->
            <h1 class="page-title">
              {{ callId }}
            </h1>
          </div>

          <div class="right-section">
            <!-- Search Input (Hidden for this page) -->
            <!-- Filter Button (Hidden for this page) -->
          </div>
        </div>
      </div>

      <!-- Body below header (not the shell .content-area) -->
      <div class="call-detail-body">
        <div class="main-content">
          <!-- Call Info and Details -->
          <div class="content-layout">
            <!-- Top Section - Call Information Table -->
            <div class="call-info-section">
              <!-- Basic Info Group -->
              <div class="info-group">
                <div class="info-row">
                  <span class="info-label">Call ID</span>
                  <div class="info-value-with-icon">
                    <span class="info-value">{{ callDetails.callId }}</span>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      @click="copyCallId"
                      class="copy-btn">
                      <v-icon size="16" color="on-surface">
                        mdi-content-copy
                      </v-icon>
                    </v-btn>
                  </div>
                </div>

                <div class="info-row">
                  <span class="info-label">Status</span>
                  <div class="status-tag" :class="`status-${callDetails.status.toLowerCase()}`">
                    {{ callDetails.status }}
                  </div>
                </div>

                <div class="info-row">
                  <span class="info-label">End Reason</span>
                  <span class="info-value">{{ callDetails.endReason }}</span>
                </div>
              </div>

              <!-- Time Info Group -->
              <div class="info-group">
                <div class="info-row">
                  <span class="info-label">Start</span>
                  <span class="info-value">{{ formatDateTime(callDetails.startTime) }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">End</span>
                  <span class="info-value">{{ formatDateTime(callDetails.endTime) }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Duration</span>
                  <span class="info-value">{{ callDetails.duration }}</span>
                </div>
              </div>

              <!-- Settings Info Group -->
              <div class="info-group">
                <div class="info-row">
                  <span class="info-label">Join Timeout</span>
                  <span class="info-value">{{ callDetails.joinTimeout }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Max Duration</span>
                  <span class="info-value">{{ callDetails.maxDuration }}</span>
                </div>

                <div class="info-row">
                  <span class="info-label">Recording Enabled</span>
                  <div class="info-value-with-icon">
                    <v-icon size="16" color="success" class="check-icon">
                      mdi-check-circle
                    </v-icon>
                    <span class="info-value">Yes</span>
                  </div>
                </div>
              </div>

              <!-- Additional Info Group -->
              <div class="info-group">
                <div class="info-row">
                  <span class="info-label">First Speaker</span>
                  <span class="info-value">{{ callDetails.firstSpeaker }}</span>
                </div>
              </div>
            </div>

            <!-- Bottom Section - Tabs Content -->
            <div class="tabs-content-section">
              <!-- Tabs Header -->
              <div class="tabs-area">
                <div class="tabs-container">
                  <div
                    v-for="tab in tabs"
                    :key="tab.id"
                    class="tab-item"
                    :class="{ active: tab.id === activeTab }"
                    @click="setActiveTab(tab.id)">
                    {{ tab.name }}
                  </div>
                </div>
              </div>

              <!-- Tab Content -->
              <div class="tab-content">
                <!-- Overview Tab -->
                <div v-if="activeTab === 'overview'" class="overview-content">
                  <!-- Summary Accordion -->
                  <div class="accordion-item">
                    <div class="accordion-header" @click="toggleAccordion('summary')">
                      <h3 class="accordion-title">
                        Summary
                      </h3>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        class="expand-btn"
                        :class="{ expanded: accordions.summary }">
                        <v-icon size="16" color="on-surface">
                          mdi-chevron-up
                        </v-icon>
                      </v-btn>
                    </div>
                    <div v-if="accordions.summary" class="accordion-content">
                      <p class="summary-text">
                        {{ callDetails.summary }}
                      </p>
                    </div>
                  </div>

                  <!-- Recording Accordion -->
                  <div class="accordion-item">
                    <div class="accordion-header" @click="toggleAccordion('recording')">
                      <h3 class="accordion-title">
                        Recording
                      </h3>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        class="expand-btn"
                        :class="{ expanded: accordions.recording }">
                        <v-icon size="16" color="on-surface">
                          mdi-chevron-up
                        </v-icon>
                      </v-btn>
                    </div>
                    <div v-if="accordions.recording" class="accordion-content recording-content">
                      <!-- Audio Player -->
                      <div class="audio-player">
                        <v-btn
                          icon
                          size="default"
                          variant="text"
                          @click="togglePlayback"
                          class="play-btn">
                          <v-icon size="20" color="on-surface">
                            {{ isPlaying ? 'mdi-pause' : 'mdi-play' }}
                          </v-icon>
                        </v-btn>

                        <div class="player-controls">
                          <div class="progress-area">
                            <v-slider
                              v-model="playbackProgress"
                              :max="100"
                              :min="0"
                              :step="0.1"
                              track-color="surface-3"
                              color="primary"
                              thumb-color="primary"
                              hide-details
                              class="audio-slider"/>
                          </div>
                          <div class="time-display">
                            <span class="current-time">{{ currentTime }}</span>
                            <span class="total-time">{{ totalTime }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Stages Accordion -->
                  <div class="accordion-item">
                    <div class="accordion-header" @click="toggleAccordion('stages')">
                      <h3 class="accordion-title">
                        Stages
                      </h3>
                      <v-btn
                        icon
                        size="small"
                        variant="text"
                        class="expand-btn"
                        :class="{ expanded: accordions.stages }">
                        <v-icon size="16" color="on-surface">
                          mdi-chevron-up
                        </v-icon>
                      </v-btn>
                    </div>
                    <div v-if="accordions.stages" class="accordion-content stages-content">
                      <!-- Timeline -->
                      <div class="stages-timeline">
                        <!-- Start Event -->
                        <div class="timeline-event">
                          <div class="timeline-line"/>
                          <div class="status-tag status-live">
                            Call Started
                          </div>
                        </div>

                        <!-- Stages List -->
                        <div class="stages-list">
                          <div
                            v-for="stage in callDetails.stages"
                            :key="stage.id"
                            class="stage-accordion">
                            <div class="stage-header" @click="toggleStage(stage.id)">
                              <h4 class="stage-title">
                                {{ stage.name }}
                              </h4>
                              <v-btn
                                icon
                                size="small"
                                variant="text"
                                class="expand-btn"
                                :class="{ expanded: expandedStages.includes(stage.id) }">
                                <v-icon size="16" color="on-surface">
                                  mdi-chevron-up
                                </v-icon>
                              </v-btn>
                            </div>
                            <div v-if="expandedStages.includes(stage.id)" class="stage-content">
                              <div class="stage-details">
                                <div class="detail-row">
                                  <span class="detail-label">Voice</span>
                                  <span class="detail-value">{{ stage.voice }}</span>
                                </div>
                                <div class="detail-row">
                                  <span class="detail-label">Temperature</span>
                                  <span class="detail-value">{{ stage.temperature }}</span>
                                </div>
                                <div class="detail-row system-prompt-row">
                                  <span class="detail-label">System Prompt</span>
                                  <p class="detail-value system-prompt">
                                    {{ stage.systemPrompt }}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- End Event -->
                        <div class="timeline-event">
                          <div class="timeline-line"/>
                          <div class="status-tag status-ended">
                            Call Ended
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Transcript Tab -->
                <div v-if="activeTab === 'transcript'" class="transcript-content">
                  <div class="coming-soon">
                    <p>Transcript content coming soon...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CallDetail',
  data() {
    return {
      callId: '1234-567-89',
      activeTab: 'overview',
      tabs: [
        { id: 'overview', name: 'Overview' },
        { id: 'transcript', name: 'Transcript' },
      ],
      accordions: {
        summary: true,
        recording: false,
        stages: false,
      },
      expandedStages: [],
      isPlaying: false,
      playbackProgress: 0,
      currentTime: '00:00:00',
      totalTime: '00:03:08',
      callDetails: {
        callId: '1234-567-89',
        status: 'Ended',
        endReason: 'Hangup',
        startTime: '2025-07-05T14:19:00',
        endTime: '2025-07-05T14:22:00',
        duration: '00:03:08',
        joinTimeout: '30s',
        maxDuration: '3600s',
        recordingEnabled: true,
        firstSpeaker: 'Agent',
        summary: 'The caller contacted the assistant for help with a specific problem. '
          + 'The assistant listened to the caller\'s concern and provided a solution, addressing all their questions and worries. '
          + 'The caller was satisfied with the assistance and the issue was resolved efficiently. '
          + 'The call ended with a positive outcome and the caller was grateful for the support.',
        stages: [
          {
            id: 1,
            name: 'Stage 1',
            voice: 'Mark',
            temperature: 0,
            systemPrompt: 'Lorem ipsum dolor sit amet consectetur. Risus fames luctus sit vel arcu amet '
              + 'lacinia placerat egestas. Cras sed integer convallis nam.',
          },
          {
            id: 2,
            name: 'Stage 2',
            voice: 'Mark',
            temperature: 0,
            systemPrompt: 'Lorem ipsum dolor sit amet consectetur. Risus fames luctus sit vel arcu amet '
              + 'lacinia placerat egestas. Cras sed integer convallis nam.',
          },
          {
            id: 3,
            name: 'Stage 3',
            voice: 'Mark',
            temperature: 0,
            systemPrompt: 'Lorem ipsum dolor sit amet consectetur. Risus fames luctus sit vel arcu amet '
              + 'lacinia placerat egestas. Cras sed integer convallis nam.',
          },
        ],
      },
    };
  },
  mounted() {
    // Get call ID from route params if available
    if (this.$route.params.id) {
      this.callId = this.$route.params.id;
      this.callDetails.callId = this.$route.params.id;
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    copyCallId() {
      navigator.clipboard.writeText(this.callDetails.callId).then(() => {
        // TODO: Show success toast
      });
    },
    setActiveTab(tabId) {
      this.activeTab = tabId;
    },
    toggleAccordion(section) {
      this.accordions[section] = !this.accordions[section];
    },
    toggleStage(stageId) {
      const index = this.expandedStages.indexOf(stageId);
      if (index > -1) {
        this.expandedStages.splice(index, 1);
      } else {
        this.expandedStages.push(stageId);
      }
    },
    togglePlayback() {
      this.isPlaying = !this.isPlaying;
      // TODO: Implement actual audio playback
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/variables.scss";
@import "../styles/colors.scss";

.call-detail-page {
  .page-content {
    .page-header {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 10px;
    }

    .call-detail-body {
      display: flex;
      flex-direction: column;
      flex: 1;

      .main-content {
        .content-layout {
          display: flex;
          flex-direction: column;
          gap: 24px;
          padding: 24px;
          flex: 1;

          .call-info-section {
            display: flex;
            gap: 24px;
            background-color: $light-surface-2; // #FFFFFF
            padding: 24px;

            .info-group {
              display: flex;
              flex-direction: column;
              gap: 12px;
              flex: 1;

              .info-row {
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 4px;

                .info-label {
                  font-family: "Plus Jakarta Sans", sans-serif;
                  font-weight: 400;
                  font-size: $body-3-size;
                  line-height: 1.2;
                  color: $light-foreground-4; // #93A0AF
                }

                .info-value {
                  font-family: "Plus Jakarta Sans", sans-serif;
                  font-weight: 600;
                  font-size: $body-3-size;
                  line-height: 1.2;
                  color: $light-foreground-1; // #171D22
                }

                .info-value-with-icon {
                  display: flex;
                  align-items: center;
                  gap: 8px;

                  .copy-btn {
                    width: 24px;
                    height: 24px;
                    background-color: $light-surface-1; // #F7F8FB
                    border-radius: 8px;
                  }

                  .check-icon {
                    margin-right: 4px;
                  }
                }

                .status-tag {
                  padding: 4px 8px;
                  border-radius: 200px;
                  font-family: "Plus Jakarta Sans", sans-serif;
                  font-weight: 600;
                  font-size: $caption-1-size;
                  line-height: 1.2;
                  border: 1px solid;
                  width: fit-content;

                  &.status-ended {
                    color: $light-foreground-1; // #171D22
                    border-color: $light-foreground-4; // #93A0AF
                    background-color: transparent;
                  }

                  &.status-live {
                    color: #00AC4F;
                    border-color: #66CD95;
                    background-color: transparent;
                  }
                }
              }
            }
          }

          .tabs-content-section {
            display: flex;
            flex-direction: column;

            .tabs-area {
              display: flex;
              flex-direction: column;
              gap: 10px;
              padding: 12px 24px 0px;
              border-bottom: 1px solid $light-border; // #D3DAE0

              .tabs-container {
                display: flex;

                .tab-item {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  gap: 10px;
                  padding: 8px 12px;
                  height: 32px;
                  font-family: "Plus Jakarta Sans", sans-serif;
                  font-weight: 600;
                  font-size: $body-3-size;
                  line-height: 1.2;
                  color: $light-foreground-4; // #93A0AF
                  cursor: pointer;
                  border-bottom: 2px solid transparent;

                  &.active {
                    color: $light-foreground-1; // #171D22
                    border-bottom-color: $light-primary; // #EB575D
                  }

                  &:hover:not(.active) {
                    color: $light-foreground-3; // #5C6A75
                  }
                }
              }
            }

            .tab-content {
              flex: 1;

              .overview-content {
                display: flex;
                flex-direction: column;
                gap: 16px;
                padding: 24px;

                .accordion-item {
                  display: flex;
                  flex-direction: column;
                  border: 1px solid $light-border; // #D3DAE0
                  border-radius: 12px;

                  .accordion-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 157px;
                    padding: 8px 12px;
                    background-color: $light-surface-1; // #F7F8FB
                    border-bottom: 1px solid $light-border; // #D3DAE0
                    cursor: pointer;

                    .accordion-title {
                      font-family: "Plus Jakarta Sans", sans-serif;
                      font-weight: 700;
                      font-size: $body-2-size;
                      line-height: 1.4;
                      color: $light-foreground-1; // #171D22
                      margin: 0;
                    }

                    .expand-btn {
                      width: 24px;
                      height: 24px;
                      background-color: $light-surface-2; // #FFFFFF
                      border-radius: 8px;
                      transition: transform 0.3s ease;

                      &.expanded {
                        transform: rotate(180deg);
                      }
                    }
                  }

                  .accordion-content {
                    padding: 12px;
                    background-color: $light-surface-2; // #FFFFFF

                    .summary-text {
                      font-family: "Plus Jakarta Sans", sans-serif;
                      font-weight: 400;
                      font-size: $body-3-size;
                      line-height: 1.6;
                      color: $light-foreground-3; // #5C6A75
                      margin: 0;
                    }

                    &.recording-content {
                      padding: 8px 40px;

                      .audio-player {
                        display: flex;
                        align-items: center;
                        gap: 12px;

                        .play-btn {
                          width: 32px;
                          height: 32px;
                          background-color: $light-surface-1; // #F7F8FB
                          border-radius: 8px;
                        }

                        .player-controls {
                          display: flex;
                          flex-direction: column;
                          flex: 1;

                          .progress-area {
                            width: 100%;
                            height: 32px;

                            .audio-slider {
                              .v-slider-track__background {
                                background-color: #E9EDF1;
                                height: 8px;
                                border-radius: 100px;
                              }

                              .v-slider-track__fill {
                                background-color: $light-primary; // #EB575D
                                height: 8px;
                                border-radius: 100px;
                              }

                              .v-slider-thumb {
                                background-color: $light-primary; // #EB575D
                              }
                            }
                          }

                          .time-display {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 8px 0px 10px;

                            .current-time,
                            .total-time {
                              font-family: "Plus Jakarta Sans", sans-serif;
                              font-weight: 400;
                              font-size: $caption-1-size;
                              line-height: 1.2;
                              color: $light-foreground-4; // #93A0AF
                            }
                          }
                        }
                      }
                    }

                    &.stages-content {
                      .stages-timeline {
                        display: flex;
                        flex-direction: column;

                        .timeline-event {
                          display: flex;
                          align-items: center;
                          gap: 12px;
                          padding: 0px 0px 0px 24px;

                          .timeline-line {
                            width: 0px;
                            height: 80px;
                            border-left: 1px dashed #B7C0CA;
                          }

                          .status-tag {
                            padding: 4px 8px;
                            border-radius: 200px;
                            font-family: "Plus Jakarta Sans", sans-serif;
                            font-weight: 600;
                            font-size: $caption-1-size;
                            line-height: 1.2;
                            border: 1px solid;
                            width: 96px;
                            text-align: center;

                            &.status-live {
                              color: #00AC4F;
                              border-color: #66CD95;
                              background-color: transparent;
                            }

                            &.status-ended {
                              color: $light-foreground-1; // #171D22
                              border-color: $light-foreground-4; // #93A0AF
                              background-color: transparent;
                            }
                          }
                        }

                        .stages-list {
                          display: flex;
                          flex-direction: column;
                          gap: 12px;

                          .stage-accordion {
                            display: flex;
                            flex-direction: column;
                            height: 151px;

                            .stage-header {
                              display: flex;
                              justify-content: space-between;
                              align-items: center;
                              gap: 157px;
                              padding: 8px 12px;
                              background-color: $light-surface-1; // #F7F8FB
                              border-bottom: 1px solid $light-border; // #D3DAE0
                              cursor: pointer;

                              .stage-title {
                                font-family: "Plus Jakarta Sans", sans-serif;
                                font-weight: 700;
                                font-size: $body-3-size;
                                line-height: 1.4;
                                color: $light-foreground-1; // #171D22
                                margin: 0;
                              }

                              .expand-btn {
                                width: 24px;
                                height: 24px;
                                background-color: $light-surface-2; // #FFFFFF
                                border-radius: 8px;
                                transition: transform 0.3s ease;

                                &.expanded {
                                  transform: rotate(180deg);
                                }
                              }
                            }

                            .stage-content {
                              display: flex;
                              flex-direction: column;
                              gap: 24px;
                              padding: 12px;
                              background-color: $light-surface-1; // #F7F8FB

                              .stage-details {
                                display: flex;
                                gap: 24px;

                                .detail-row {
                                  display: flex;
                                  flex-direction: column;
                                  justify-content: center;
                                  gap: 4px;
                                  width: 356px;
                                  height: 45px;

                                  &.system-prompt-row {
                                    width: 100%;
                                    height: auto;

                                    .system-prompt {
                                      line-height: 1.6;
                                    }
                                  }

                                  .detail-label {
                                    font-family: "Plus Jakarta Sans", sans-serif;
                                    font-weight: 400;
                                    font-size: $body-3-size;
                                    line-height: 1.2;
                                    color: $light-foreground-4; // #93A0AF
                                  }

                                  .detail-value {
                                    font-family: "Plus Jakarta Sans", sans-serif;
                                    font-weight: 600;
                                    font-size: $body-3-size;
                                    line-height: 1.2;
                                    color: $light-foreground-1; // #171D22
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }

              .transcript-content {
                padding: 24px;

                .coming-soon {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 200px;

                  p {
                    font-family: "Plus Jakarta Sans", sans-serif;
                    font-weight: 400;
                    font-size: $body-2-size;
                    color: $light-foreground-4; // #93A0AF
                    margin: 0;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

// Scrollbar styling
:deep(.v-slider) {
  .v-slider-track__background {
    background-color: #E9EDF1 !important;
    height: 8px !important;
    border-radius: 100px !important;
  }

  .v-slider-track__fill {
    background-color: $light-primary !important; // #EB575D
    height: 8px !important;
    border-radius: 100px !important;
  }

  .v-slider-thumb {
    background-color: $light-primary !important; // #EB575D
  }
}
</style>
